import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../utils/apiClient';
import * as logger from '../utils/logger';

/**
 * Connects to quote2cart_api /ws/chat using a BFF-minted ticket.
 * REST remains the write path; this hook only receives events.
 */
export function useChatSocket({ enabled = true, onEvent } = {}) {
  const [status, setStatus] = useState('idle');
  const onEventRef = useRef(onEvent);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const disconnect = useCallback(() => {
    stoppedRef.current = true;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        /* ignore */
      }
      wsRef.current = null;
    }
    setStatus('idle');
  }, []);

  const connect = useCallback(async () => {
    if (!enabled || stoppedRef.current) return;
    setStatus('connecting');
    try {
      const ticketRes = await api.getChatWsTicket();
      const override = (import.meta.env.VITE_QUOTE2CART_WS_URL || '').replace(/\/$/, '');
      let wsUrl = ticketRes?.wsUrl;
      if (override && ticketRes?.ticket) {
        wsUrl = `${override}/ws/chat?ticket=${encodeURIComponent(ticketRes.ticket)}`;
      }
      if (!wsUrl) throw new Error('No WebSocket URL from ticket');

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        setStatus('open');
      };
      ws.onmessage = (ev) => {
        let data = null;
        try {
          data = JSON.parse(String(ev.data));
        } catch {
          return;
        }
        if (data?.type === 'ping') {
          try {
            ws.send(JSON.stringify({ type: 'ping' }));
          } catch {
            /* ignore */
          }
          return;
        }
        onEventRef.current?.(data);
      };
      ws.onerror = () => {
        logger.error('chat socket error');
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (stoppedRef.current || !enabled) {
          setStatus('idle');
          return;
        }
        setStatus('reconnecting');
        const attempt = retryRef.current + 1;
        retryRef.current = attempt;
        const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5));
        timerRef.current = setTimeout(() => {
          void connect();
        }, delay);
      };
    } catch (err) {
      logger.error('chat socket connect failed', err);
      setStatus('error');
      if (!stoppedRef.current && enabled) {
        const attempt = retryRef.current + 1;
        retryRef.current = attempt;
        const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5));
        timerRef.current = setTimeout(() => {
          void connect();
        }, delay);
      }
    }
  }, [enabled]);

  useEffect(() => {
    stoppedRef.current = false;
    if (enabled) void connect();
    return () => disconnect();
  }, [enabled, connect, disconnect]);

  return { status, reconnect: connect, disconnect };
}
